/**
 * ENTERPRISE360 — the value-stream methodology / reinvention engine page (one of the
 * 360 family). Explains: classify by archetype → apply sector/complexity
 * overlays → assemble a function-by-function blueprint with editable maps.
 * Built on the existing `ek-` design tokens — no new colour/typography system.
 */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Download, Layers, Building2, Map, Boxes, ShieldCheck } from "lucide-react";
import StarMotif from "../components/StarMotif";
import { ENGINE } from "../data/family360";

const HERO_BG =
  "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGNvbm5lY3RlZCUyMG5ldHdvcmslMjBub2RlcyUyMGRhcmt8ZW58MHx8fHwxNzgyNTkyMDE3fDA&ixlib=rb-4.1.0&q=85";

const PDF_URL = `${process.env.REACT_APP_BACKEND_URL}/api/docs/public/ekselens360-overview.pdf`;

const STEPS = [
  { icon: Building2, title: "Classify by archetype",
    body: "A short profile resolves the business to a named archetype — e.g. Global Regulated Manufacturer, Regulated Financial Services, Pharma / MedTech. The archetype is the recipe: which value streams matter, and how deep to go." },
  { icon: Layers, title: "Apply sector & complexity overlays",
    body: "Sector, geography, supplier-tiering, control and ESG overlays tailor the design — so a bank cites the PRA/FCA and DORA, and a life-sciences plan cites the MHRA and GxP. Sector-aware, never generic." },
  { icon: Boxes, title: "Assemble the blueprint",
    body: "The engine composes the relevant value streams into one tailored, function-by-function Reinvention Kit — pulling the twelve specialist lenses in as deep-dives where they add value." },
  { icon: Map, title: "Editable process maps & RAID",
    body: "Every function ships with editable end-to-end maps (L1–L4 BPMN), a pre-populated risk register, a capability heatmap and a clear target operating model." },
];

// Illustrative function-by-function blueprint (mocked, on-brand).
const STREAMS = ["Idea-to-Market", "Plan-to-Produce", "Source-to-Pay", "Lead-to-Order", "Order-to-Cash", "Record-to-Report"];

export default function Enterprise360() {
  return (
    <div data-testid="enterprise360-page">
      {/* Hero */}
      <section className="relative ek-gradient-dark text-white overflow-hidden" data-testid="enterprise360-hero">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-black/70" />
        </div>
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up">System of Proof · the value-stream methodology</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 max-w-4xl ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="enterprise360-headline">
            ENTERPRISE360.<br /><span className="text-[#A179FA]">The reinvention engine.</span>
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-7 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }}>
            Classify a business by archetype, apply sector & complexity overlays, and assemble a
            tailored, function-by-function reinvention blueprint — the one toolset that ties the
            other twelve together.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 ek-fade-up" style={{ animationDelay: "180ms" }}>
            <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary" data-testid="enterprise360-vs-cta">
              Assess a value stream — free <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="ek-btn ek-btn-outline-light" data-testid="enterprise360-contact-cta">
              Book a discovery conversation
            </Link>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="ek-btn ek-btn-outline-light" data-testid="enterprise360-pdf-cta">
              <Download size={16} /> Download the overview (PDF)
            </a>
          </div>
          <div className="flex flex-wrap gap-3 mt-7 ek-fade-up" style={{ animationDelay: "220ms" }}>
            <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5" data-testid="enterprise360-stat-sectors">{ENGINE.sectors} sectors</span>
            <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5" data-testid="enterprise360-stat-archetypes">{ENGINE.archetypes} business archetypes</span>
            <span className="text-[13px] bg-white/10 text-white rounded-full px-4 py-1.5">Governed learning loop</span>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-white ek-section" data-testid="enterprise360-how">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
            <div className="lg:col-span-5">
              <div className="ek-eyebrow">How the engine works</div>
              <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">A business is assembled,<br /> not guessed.</h2>
            </div>
            <div className="lg:col-span-7">
              <p className="ek-body text-lg max-w-2xl">
                A real enterprise is a combination of value streams. ENTERPRISE360 turns that
                reality into a plan — deterministically, so every deliverable stays consistent,
                with AI used only to enrich the structure, never to invent it.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ek-stagger">
            {STEPS.map((s, i) => (
              <div key={s.title} className="ek-card group" data-testid={`enterprise360-step-${i}`}>
                <div className="flex items-start justify-between mb-5">
                  <div className="w-10 h-10 flex items-center justify-center bg-[#F2F2F3] text-[#7737F6] group-hover:bg-[#7737F6] group-hover:text-white transition-colors">
                    <s.icon size={20} strokeWidth={1.5} />
                  </div>
                  <div className="text-xs font-semibold tracking-[0.18em] text-zinc-400 uppercase">Step 0{i + 1}</div>
                </div>
                <h3 className="ek-heading text-xl md:text-[22px] text-black leading-snug">{s.title}</h3>
                <p className="ek-body text-[15px] mt-3">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Illustrative blueprint */}
      <section className="ek-gradient-dark-soft text-white ek-section relative overflow-hidden" data-testid="enterprise360-blueprint">
        <div className="ek-star-watermark-left" />
        <div className="ek-container relative z-10">
          <div className="ek-eyebrow text-[#A179FA]">The Reinvention Kit</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-white max-w-3xl">A function-by-function blueprint.</h2>
          <p className="text-zinc-300 font-light text-lg leading-relaxed mt-5 max-w-2xl">
            Each active value stream becomes a tailored chapter, with editable L1–L4 process maps
            you can open in Visio, drawio or bpmn.io. Below is an illustrative shape of a blueprint.
          </p>
          <div className="mt-10 border border-white/15 bg-white/[0.04] p-6 md:p-10" data-testid="enterprise360-blueprint-map">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              {STREAMS.map((s, i) => (
                <div key={s} className="relative">
                  <div className="border border-[#7737F6]/50 bg-[#7737F6]/10 p-4 text-center">
                    <div className="text-[10px] tracking-[0.16em] uppercase text-[#A179FA]">L1</div>
                    <div className="text-[13px] font-medium text-white mt-1 leading-tight">{s}</div>
                  </div>
                  <div className="mt-2 space-y-1.5">
                    {["L2 process", "L3 lanes", "L4 procedures"].map((lvl) => (
                      <div key={lvl} className="border border-white/10 bg-black/30 px-2 py-1.5 text-[11px] text-zinc-400 flex items-center gap-1.5">
                        <span className="w-1.5 h-1.5 bg-[#A179FA] rounded-full shrink-0" /> {lvl}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <p className="text-[11px] text-zinc-500 mt-6 italic">Illustrative only — your blueprint activates the value streams your archetype requires.</p>
          </div>
        </div>
      </section>

      {/* Coverage / specialists */}
      <section className="bg-white ek-section" data-testid="enterprise360-coverage">
        <div className="ek-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <div className="ek-eyebrow">Powered by the family</div>
              <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black">The twelve lenses become<br /> the specialist chapters.</h2>
              <p className="ek-body text-lg mt-5 max-w-xl">
                SUPPLIER360 deep-dives source-to-pay, OPERATING360 the operating model, AI360 and
                DATA360 the enabling technology — so each chapter of the blueprint carries genuine
                specialist depth, all inside one EKSELENS360 lifecycle.
              </p>
              <Link to="/the-360-family" className="ek-btn ek-btn-primary mt-8 inline-flex" data-testid="enterprise360-family-cta">
                See the full 360 family <ArrowRight size={16} />
              </Link>
            </div>
            <div className="lg:col-span-6">
              <div className="grid grid-cols-2 gap-px bg-zinc-200">
                {[
                  { icon: Building2, k: `${ENGINE.archetypes} archetypes`, v: "Recipes over value streams" },
                  { icon: Layers, k: `${ENGINE.sectors} sectors`, v: "Regulators, standards & RAID built in" },
                  { icon: Map, k: "L1–L4 maps", v: "Editable BPMN per function" },
                  { icon: ShieldCheck, k: "Single source of truth", v: "Consistent across every deliverable" },
                ].map((c) => (
                  <div key={c.k} className="bg-white p-7">
                    <c.icon size={22} className="text-[#7737F6]" strokeWidth={1.5} />
                    <div className="ek-heading text-lg text-black mt-4">{c.k}</div>
                    <div className="ek-body text-[13.5px] mt-1">{c.v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white ek-section" data-testid="enterprise360-cta">
        <div className="ek-container text-center max-w-3xl">
          <StarMotif size={26} color="#A179FA" className="mx-auto mb-6" />
          <h2 className="ek-heading text-4xl md:text-5xl text-white">Ready to build your blueprint?</h2>
          <p className="text-zinc-300 font-light text-lg mt-5">
            See the EKSELENS360 method — and the ENTERPRISE360 engine — applied to your business.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary" data-testid="enterprise360-final-vs">
              Start with one value stream <ArrowRight size={16} />
            </Link>
            <Link to="/contact" className="ek-btn ek-btn-outline-light" data-testid="enterprise360-final-contact">
              Book a discovery conversation
            </Link>
            <a href={PDF_URL} target="_blank" rel="noopener noreferrer" className="ek-btn ek-btn-outline-light" data-testid="enterprise360-final-pdf">
              <Download size={16} /> Download the overview (PDF)
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
